import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import Button from "./Button";
import BackButton from "./BackButton";

/**
 * Bun slide panel: right-side quick view, "detail without the page hop".
 * Spec: Component Library "Overlays". Scrim + white panel + left-cast shadow.
 */
export default function SlidePanel({
  open,
  onClose,
  initialFocus,
  title,
  subtitle,
  badge,
  leading,
  mobileSheet = false,
  side = "right",
  variant,
  hideTitle = false,
  headerActions,
  toolbar,
  footer,
  width = 420,
  zIndex = 50,
  closeLabel = "Close panel",
  backLabel = "Back",
  closeButtonVariant = "secondary",
  panelClassName,
  bodyClassName,
  panelProps,
  bodyProps,
  children,
}) {
  return (
    <Transition show={!!open} as={Fragment}>
      <Dialog onClose={onClose} initialFocus={initialFocus} className="relative font-instrument" style={{ zIndex }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[color:var(--bun-scrim)]" aria-hidden="true" />
        </Transition.Child>
        <div className={cn('fixed flex max-w-full', side === 'left' ? 'left-0' : 'right-0', mobileSheet ? 'inset-x-0 bottom-0 sm:left-auto sm:top-0' : 'inset-y-0')}>
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-200"
            enterFrom={mobileSheet ? 'translate-y-full sm:translate-y-0 sm:translate-x-full' : side === 'left' ? '-translate-x-full' : 'translate-x-full'}
            enterTo="translate-x-0 translate-y-0"
            leave="transform transition ease-in duration-150"
            leaveFrom="translate-x-0 translate-y-0"
            leaveTo={mobileSheet ? 'translate-y-full sm:translate-y-0 sm:translate-x-full' : side === 'left' ? '-translate-x-full' : 'translate-x-full'}
          >
            <Dialog.Panel
              {...panelProps}
              data-bun-dialog-panel
              style={mobileSheet ? { '--panel-width': `${width}px` } : { width: variant === 'detail' ? '100vw' : width }}
              className={cn(
                "max-w-full bg-bun-surface text-bun-text shadow-bun-panel flex flex-col",
                mobileSheet ? 'w-full max-h-[90dvh] rounded-t-bun-xl sm:w-[var(--panel-width)] sm:h-full sm:max-h-none sm:rounded-none' : 'h-full',
                variant === "navigation" && "max-w-[calc(100vw-40px)]",
                panelClassName
              )}
            >
              {mobileSheet && <div aria-hidden="true" className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-bun-track-off sm:hidden" />}
              <div
                className={cn(
                  "flex items-center gap-[10px] px-5 py-4 border-b border-bun-line shrink-0",
                  variant === "navigation" && "border-0 px-[18px] pt-[max(18px,env(safe-area-inset-top))] pb-[14px]",
                  variant === "detail" && "grid grid-cols-[1fr_minmax(0,2fr)_1fr] pt-[max(16px,env(safe-area-inset-top))]"
                )}
              >
                {variant === 'detail' ? <BackButton compactOnMobile={false} onClick={() => onClose(false)}>{backLabel}</BackButton> : leading}
                <div className={cn("flex-1 min-w-0", variant === 'detail' && 'text-center')}>
                  {title && (
                    <Dialog.Title className={cn("m-0 font-instrument text-bun-heading font-semibold leading-snug tracking-normal truncate", hideTitle && "sr-only")}>
                      {title}
                    </Dialog.Title>
                  )}
                  {subtitle && (
                    <p className="m-0 text-bun-meta font-book text-bun-stone truncate">{subtitle}</p>
                  )}
                </div>
                {badge}
                {headerActions}
                {variant !== 'detail' && <Button
                  variant={closeButtonVariant}
                  size="sm"
                  iconOnly
                  className={variant === 'navigation' ? 'sm:min-h-11 sm:min-w-11' : undefined}
                  onClick={() => onClose(false)}
                  aria-label={closeLabel}
                >
                  <XIcon className="w-[13px] h-[13px]" />
                </Button>}
              </div>
              {toolbar && <div className="shrink-0 max-h-[50dvh] overflow-y-auto px-5 pt-3">{toolbar}</div>}
              <div
                {...bodyProps}
                className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", variant === "navigation" && "flex min-h-0 flex-col px-3 pt-0 pb-[max(18px,env(safe-area-inset-bottom))]", bodyClassName, bodyProps?.className)}
              >
                {children}
              </div>
              {footer && (
                <div
                  className={cn(
                    "px-5 py-3 border-t border-bun-line shrink-0",
                    variant === "detail" && "pb-[max(12px,env(safe-area-inset-bottom))]"
                  )}
                >
                  {footer}
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
